import {
  DEFAULT_COUNTRY_ISO2,
  getCountryByIso2,
  getDefaultCountry,
  SUPPORTED_COUNTRIES,
  type SupportedCountry,
} from '@/lib/config-system/countries';

/**
 * Platform-wide phone normalization — one algorithm, shared by the browser and
 * the server.
 *
 * The goal is a single canonical E.164 string (`+<dial><national>`) with no
 * trunk `0`, no redundant dial code, and no `+`/`00` ambiguity, so identity
 * joins, OTP hashing, loyalty lookups, and mobile-money charges all agree on the
 * same key. This module is intentionally dependency-light and must stay free of
 * `server-only` and of `libphonenumber-js`: it runs in the sign-up modal too.
 * Strict carrier validity is a separate, server-only gate (see
 * `lib/server/phone-normalize.ts`).
 *
 * The rules, in order:
 *  1. `+` or `00` prefix  -> already international; strip `00`.
 *  2. bare digits that start with the selected region's dial code (and leave at
 *     least a 7-digit subscriber) -> a redundant dial code, keep it
 *     (`2348031234567` -> `+2348031234567`).
 *  3. bare digits that start with a *different* known dial code, are longer than
 *     a local subscriber (11+), and leave a 7-digit subscriber -> international.
 *  4. everything else -> read the first `0` as the region's trunk prefix and
 *     prepend the region dial (`0551234567` -> `+233551234567`).
 *
 * Step 3 is deliberately conservative: a genuine local number is never 11+
 * digits, and the "longer than 11 / at least 7 left" guard keeps short local
 * pastes from being misread as a foreign dial code.
 */
export type PhoneNormalizeError = 'empty' | 'too_short' | 'too_long';

export type PhoneNormalizeResult =
  | {
      ok: true;
      /** Canonical `+<dial><national>`, e.g. `+233551234567`. */
      readonly e164: string;
      /** Significant national digits after the dial code, no trunk `0`. */
      readonly national: string;
      /** The dial-code owner, or `null` when the dial code is not in the table. */
      readonly country: SupportedCountry | null;
    }
  | { ok: false; readonly reason: PhoneNormalizeError; readonly message: string };

// E.164 caps a number at 15 digits; the platform floor is 8 so a 7-digit paste
// (`+233` + a 5-digit stub) is still rejected.
const MIN_E164_DIGITS = 8;
const MAX_E164_DIGITS = 15;

// A bare national number longer than this can only be an international form.
const MAX_BARE_LOCAL_DIGITS = 10;

// A bare international form must still leave a plausible subscriber number.
const MIN_SUBSCRIBER_DIGITS = 7;

function fail(reason: PhoneNormalizeError, message: string): PhoneNormalizeResult {
  return { ok: false, reason, message };
}

/**
 * Find the country whose dial code is the longest prefix of `digits`. Longest
 * wins because dial codes are not prefix-free (`1` for US/CA vs `1` inside other
 * codes); `getSupportedDialCodes()` is sorted the same way.
 */
function findDialPrefix(
  digits: string,
  table: readonly SupportedCountry[]
): { country: SupportedCountry; dial: string } | null {
  let best: { country: SupportedCountry; dial: string } | null = null;
  for (const country of table) {
    if (
      digits.startsWith(country.dial) &&
      (!best || country.dial.length > best.dial.length)
    ) {
      best = { country, dial: country.dial };
    }
  }
  return best;
}

/** Split an international digit string into `{ dial, national, country }`. */
function splitInternational(
  digits: string,
  table: readonly SupportedCountry[]
): { dial: string; national: string; country: SupportedCountry | null } {
  const match = findDialPrefix(digits, table);
  if (!match) {
    return { dial: '', national: digits.replace(/^0+/, ''), country: null };
  }
  return {
    dial: match.dial,
    national: digits.slice(match.dial.length).replace(/^0+/, ''),
    country: match.country,
  };
}

/**
 * Normalize a raw phone string to canonical E.164.
 *
 * @param rawPhone   whatever the user typed/pasted, or a stored value.
 * @param countryIso2 the selected/session country (ISO2). Falls back to the
 *                    platform default when missing or unsupported.
 * @param countryTable override the supported-country table (tests).
 */
export function normalizePhoneToE164(
  rawPhone: string | null | undefined,
  countryIso2?: string | null,
  countryTable: readonly SupportedCountry[] = SUPPORTED_COUNTRIES
): PhoneNormalizeResult {
  const input = (rawPhone ?? '').trim();
  if (!input) return fail('empty', 'Enter a phone number.');

  const region =
    getCountryByIso2(countryIso2, countryTable) ??
    getCountryByIso2(DEFAULT_COUNTRY_ISO2, countryTable) ??
    getDefaultCountry(countryTable);

  const digits = input.replace(/\D/g, '');
  if (!digits) return fail('empty', 'Enter a phone number.');

  const hasPlus = input.startsWith('+');
  const hasDoubleZero = input.startsWith('00');

  let international: string;
  if (hasPlus || hasDoubleZero) {
    // Already international; `00` is the international access prefix.
    international = hasDoubleZero ? digits.replace(/^00/, '') : digits;
  } else if (
    region &&
    digits.startsWith(region.dial) &&
    digits.length - region.dial.length >= MIN_SUBSCRIBER_DIGITS
  ) {
    // A redundant dial code with no `+` (`2348031234567`).
    international = digits;
  } else {
    const local = digits.replace(/^0+/, '');
    const foreign = region ? findDialPrefix(digits, countryTable) : null;
    const looksForeign =
      !!foreign &&
      foreign.dial !== region?.dial &&
      digits.length > MAX_BARE_LOCAL_DIGITS &&
      digits.length - foreign.dial.length >= MIN_SUBSCRIBER_DIGITS;
    // Only treat a bare number as international when the dial differs from the
    // region's own — otherwise a local number that merely contains the region's
    // digits would be read twice.
    international = looksForeign
      ? digits
      : `${region?.dial ?? ''}${local}`;
  }

  const split = splitInternational(international, countryTable);
  const total = split.dial.length + split.national.length;

  if (!split.national || total < MIN_E164_DIGITS) {
    return fail('too_short', 'That phone number is too short.');
  }
  if (total > MAX_E164_DIGITS) {
    return fail('too_long', 'That phone number is too long.');
  }

  return {
    ok: true,
    e164: `+${split.dial}${split.national}`,
    national: split.national,
    country: split.country,
  };
}

/**
 * Best-effort country from a BCP-47 locale (`navigator.language` or a stored
 * preference), e.g. `en-GH` -> `GH`, `fr-CI` -> `null` (unsupported). Only the
 * supporting registry decides what is valid; an unknown region returns `null`
 * and the caller falls back to `DEFAULT_COUNTRY_ISO2`.
 *
 * We use locale, never geolocation: IP geolocation mislocates VPN/roaming
 * users and would silently pick the wrong dial code.
 */
export function detectCountryFromLocale(locale?: string | null): string | null {
  if (!locale) return null;
  const normalized = locale.trim().replace(/_/g, '-');
  if (!normalized) return null;

  const parts = normalized.split('-').filter(Boolean);
  const candidate = parts.length > 1 ? parts[parts.length - 1] : parts[0];
  if (!/^[A-Za-z]{2}$/.test(candidate)) return null;
  const iso2 = candidate.toUpperCase();
  return getCountryByIso2(iso2) ? iso2 : null;
}

/**
 * Human-readable national form for a canonical E.164 value + country
 * (`+233591919436` + `GH` -> `0591919436`), used by mobile-money display.
 * Returns the trimmed input unchanged when it does not match the dial code.
 */
export function formatNationalForCountry(
  e164: string,
  countryIso2?: string | null
): string {
  const country = getCountryByIso2(countryIso2);
  if (country && e164.startsWith(`+${country.dial}`)) {
    return `0${e164.slice(1 + country.dial.length)}`;
  }
  return e164;
}
